import { useState } from "react";
import { TRowItem } from "../components/Table/TableRow";

/**
 * Custom hook to manage table row selection state.
 * @param {TRowItem[]} dataset - The dataset representing all table rows.
 * @param {boolean} [multiSelected=true] - Determines if multiple rows can be selected simultaneously.
 * @returns {Object} Hook utilities for managing table row selection.
 * @property {TRowItem[]} selectedRowItems - The currently selected row items.
 * @property {Function} toggleRow - Toggles the selection of a single row item.
 * @property {Function} toggleDeSelectRow - Deselects a single row item.
 * @property {Function} toggleSelectAllRow - Toggles selection of all rows in the dataset.
 * @property {Function} resetSelectedRowItems - Resets the selection, clearing all selected items.
 */
export const useMangeTableItems = (
  dataset: TRowItem[],
  multiSelected: boolean = true,
) => {
  const [selectedRowItems, setSelectedRowItems] = useState<TRowItem[]>([]);
  const [allRowsSelected, updateState] = useState<Boolean>(false);

  /**
   * Toggles the selection state of a single row item.
   * If `multiSelected` is false, it replaces the selected items with the current item.
   * Otherwise, it adds or removes the item from the selected items.
   * @param {TRowItem} item - The row item to toggle.
   */
  const toggleRow = (item: TRowItem): void | { isSelected: boolean } => {
    setSelectedRowItems((prevSelected) => {
      if (!multiSelected) {
        return prevSelected.includes(item) ? [] : [item];
      } else {
        return prevSelected.includes(item)
          ? prevSelected.filter((i) => i !== item)
          : [...prevSelected, item];
      }
    });

    return {
      isSelected: !selectedRowItems.includes(item),
    };
  };

  /**
   * Deselects a single row item, removing it from the selected items.
   * @param {TRowItem} item - The row item to deselect.
   */
  const toggleDeSelectRow = (item: TRowItem) => {
    setSelectedRowItems((prevSelected) =>
      prevSelected.filter((i) => i !== item),
    );
  };
  /**
   * Toggles the selection state of all rows in the dataset.
   * If all rows are currently selected, it clears the selection.
   * Otherwise, it selects all rows in the dataset.
   */
  const toggleSelectAllRow = () => {
    if (!multiSelected) {
      return;
    } else if (allRowsSelected || selectedRowItems.length > 0) {
      setSelectedRowItems([]);
      updateState(false);
    } else {
      setSelectedRowItems(dataset);
      updateState(true);
    }
  };

  const addRows = (items: TRowItem[]) => {
    setSelectedRowItems(items);
  };
  /**
   * Resets the selection by clearing all selected items.
   */
  const resetSelectedRowItems = () => setSelectedRowItems([]);
  return {
    selectedRowItems,
    toggleDeSelectRow,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
    addRows,
  };
};
